import {
  BoothRequest,
  PatchedBoothRequest,
  ResourceRequest,
  createBooths,
  createResources,
  createSponsorResources,
  destroyBooths,
  destroyResources,
  listBooths,
  partialUpdateBooths,
  partialUpdateResources,
  partialUpdateSponsorBooths,
  retrieveSponsorBooths,
  updateRanksBooths
} from '@goldcast/api/backend';

import { Payload, Booth, updateResourcePayload } from './types';
import { transformServerBooths } from './utils';

const actions = {
  async loadBooths({ dispatch, commit }, eventId: string) {
    commit('setIsLoading', true);

    try {
      const boothsData = await listBooths({
        queryParams: {
          event: eventId
        }
      });

      const booths = transformServerBooths(boothsData);
      commit('setBooths', booths);
    } catch (error) {
      dispatch(
        'studioToast/triggerToast',
        { msg: 'Error Loading Rooms, Please Try Again', mode: 'error' },
        { root: true }
      );
    } finally {
      commit('setIsLoading', false);
    }
  },

  async createBooth({ rootState }, payload: Payload) {
    const eventId: string = rootState.studioEventList.currentEventId;
    const body = {
      ...payload,
      event: eventId
    } as BoothRequest;

    return new Promise((resolve, reject) => {
      createBooths({
        body
      })
        .then(booth => {
          resolve(booth);
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  async deleteBooth({ commit }, boothId: string) {
    return new Promise((resolve, reject) => {
      destroyBooths({
        id: boothId
      })
        .then(() => {
          commit('deleteBooth', boothId);
          resolve(boothId);
        })
        .catch(reject);
    });
  },

  async updateBooth({ commit }, payload: Payload) {
    const body = payload as PatchedBoothRequest;

    return new Promise((resolve, reject) => {
      partialUpdateBooths({
        id: payload.id,
        body
      })
        .then(response => {
          commit('setBooth', response);
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  async createResource({ rootState }, payload: ResourceRequest) {
    const eventId: string = rootState.studioEventList.currentEventId;
    const body = { ...payload, event: eventId };

    return createResources({
      body
    });
  },

  async deleteResource(_, resourceId: string) {
    return destroyResources({
      id: resourceId
    });
  },

  async updateResource(_, payload: updateResourcePayload) {
    return partialUpdateResources({
      id: payload.id,
      body: payload.body
    });
  },

  reorderBooths({ commit, rootGetters }, booths: Booth[]) {
    const newData = {
      object_list: booths.map((room, index) => {
        return {
          pk: room.id,
          rank: index
        };
      })
    };
    const prevBooths = [
      ...rootGetters['studioBooths/getPrimaryBooths'],
      ...rootGetters['studioBooths/getSecondaryBooths']
    ];
    commit('reorderBooths', booths);

    return new Promise((resolve, reject) => {
      updateRanksBooths({
        body: newData
      })
        .then(() => {
          resolve('success');
        })
        .catch((error: any) => {
          commit('reorderBooths', prevBooths);
          reject(error);
        });
    });
  },

  /**
   *  sponsors public API
   */
  async loadSponsorBooth({ commit }, boothId: string) {
    return new Promise((resolve, reject) => {
      retrieveSponsorBooths({
        id: boothId
      })
        .then(booth => {
          commit('setBooth', booth);
          resolve(booth);
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  /**
   *  sponsors public API
   */
  async updateSponsorBooth({ commit, rootGetters }, payload) {
    const sponsorUser = rootGetters['user/getSponsorUser'];
    payload.email = sponsorUser?.email;

    return new Promise((resolve, reject) => {
      partialUpdateSponsorBooths({
        id: payload.id,
        body: payload
      })
        .then(booth => {
          commit('setBooth', booth);
          resolve(booth);
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  async createSponsorBoothResource({ rootState }, payload: Payload) {
    const eventId: string = rootState.studioEventList.currentEventId;
    const body = { ...payload, event: eventId };
    return createSponsorResources({ body });
  }
};

export default actions;
